import Constants from './Constants';

// Applies the player's type setting to the document, and caches it locally.
//
// The setting itself belongs to the account -- the server is the only source of
// truth for it. What lives in localStorage is a paint hint: the profile that
// carries the real value does not arrive until several requests into the load,
// and without a hint a classic player would watch the whole page render in the
// new face and then swap. The hint is written from the server's answer, never
// the other way round, so a stale or hand-edited one is corrected within a
// second of load and can never change what is stored.

const STORAGE_KEY = 'ten-words-typography';
const CLASSIC_FONT_ID = 'typography-classic-font';
const CLASSIC_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;500&display=swap';

class Typography {
  static isValid = (typography) => {
    return typography === Constants.TYPOGRAPHY_MODERN || typography === Constants.TYPOGRAPHY_CLASSIC;
  }

  // Rubik is fetched only if somebody actually switches, so the default setting
  // -- the one we are trying to move everyone onto -- pays nothing for the
  // existence of the toggle.
  static loadClassicFont = () => {
    if (document.getElementById(CLASSIC_FONT_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = CLASSIC_FONT_ID;
    link.rel = 'stylesheet';
    link.href = CLASSIC_FONT_HREF;
    document.head.appendChild(link);
  }

  static apply = (typography) => {
    const setting = Typography.isValid(typography) ? typography : Constants.TYPOGRAPHY_MODERN;

    if (setting === Constants.TYPOGRAPHY_CLASSIC) {
      Typography.loadClassicFont();
    }

    document.documentElement.setAttribute('data-typography', setting);

    return setting;
  }

  // Storage throws rather than no-ops in a few browser configurations, so every
  // access is guarded: a player with cookies blocked still gets the setting,
  // just a frame later.
  static readHint = () => {
    try {
      const hint = window.localStorage.getItem(STORAGE_KEY);
      return Typography.isValid(hint) ? hint : null;
    } catch (error) {
      return null;
    }
  }

  static writeHint = (typography) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, typography);
    } catch (error) {
      // A missing hint costs a flash of the wrong face, nothing more.
    }
  }

  static clearHint = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // As above.
    }
  }
}

export default Typography;
